/**
 * NYT 2024 Precinct Election Results API client.
 *
 * Fetches per-state gzip-compressed TopoJSON from int.nyt.com,
 * decompresses via DecompressionStream API, and converts to GeoJSON
 * via topojson-client.
 *
 * License: C-UDA (non-commercial only) — demo/development use only.
 * Source: https://github.com/nytimes/presidential-precinct-map-2024
 */

import { feature } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';

const NYT_BASE = 'https://int.nyt.com/newsgraphics/elections/map-data/2024';

export interface PrecinctProperties {
  state: string;
  GEOID: string;
  votes_dem: number;
  votes_rep: number;
  votes_total: number;
  pct_dem_lead: number;       // Positive = Dem lead, Negative = Rep lead
  official_boundary: boolean;
}

/** State-level data cache to avoid refetching */
const stateCache = new Map<string, GeoJSON.FeatureCollection>();

/**
 * Decompress a gzip Response using the native DecompressionStream API.
 * Falls back to reading the raw body if DecompressionStream is unavailable.
 */
async function decompressResponse(response: Response): Promise<string> {
  if ('DecompressionStream' in window) {
    const ds = new DecompressionStream('gzip');
    const decompressed = response.body!.pipeThrough(ds);
    const reader = decompressed.getReader();
    const decoder = new TextDecoder();
    let result = '';
    let done = false;
    while (!done) {
      const chunk = await reader.read();
      done = chunk.done;
      if (chunk.value) {
        result += decoder.decode(chunk.value, { stream: !done });
      }
    }
    return result;
  }
  // Fallback: assume server sent uncompressed (shouldn't happen)
  return response.text();
}

/**
 * Fetch precinct election results for a US state.
 *
 * @param stateCode Two-letter state code (e.g., 'al', 'tx', 'ca')
 * @returns GeoJSON FeatureCollection with PrecinctProperties on each feature
 */
export async function fetchStatePrecincts(
  stateCode: string,
): Promise<GeoJSON.FeatureCollection> {
  const code = stateCode.toLowerCase();

  // Return cached data if available
  if (stateCache.has(code)) {
    return stateCache.get(code)!;
  }

  // Files are in national/ dir with UPPERCASE state codes
  const upper = code.toUpperCase();
  const url = `${NYT_BASE}/national/${upper}-precincts-with-results.topojson.gz`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch precinct data for ${code}: ${res.status}`);
  }

  const text = await decompressResponse(res);
  const topology: Topology = JSON.parse(text);

  // The TopoJSON has one object — get the first one
  const objectName = Object.keys(topology.objects)[0];
  const geojson = feature(
    topology,
    topology.objects[objectName] as GeometryCollection,
  ) as unknown as GeoJSON.FeatureCollection;

  stateCache.set(code, geojson);
  return geojson;
}

/**
 * Approximate US state bounding boxes [west, south, east, north].
 * Used to determine which state(s) intersect the current map viewport.
 */
export const STATE_BOUNDS: Record<string, [number, number, number, number]> = {
  al: [-88.47, 30.22, -84.89, 35.01],
  ak: [-179.15, 51.21, -129.98, 71.39],
  az: [-114.81, 31.33, -109.04, 37.00],
  ar: [-94.62, 33.00, -89.64, 36.50],
  ca: [-124.41, 32.53, -114.13, 42.01],
  co: [-109.06, 36.99, -102.04, 41.00],
  ct: [-73.73, 40.98, -71.79, 42.05],
  de: [-75.79, 38.45, -75.05, 39.84],
  dc: [-77.12, 38.79, -76.91, 38.99],
  fl: [-87.63, 24.52, -80.03, 31.00],
  ga: [-85.61, 30.36, -80.84, 35.00],
  hi: [-160.24, 18.91, -154.81, 22.24],
  id: [-117.24, 41.99, -111.04, 49.00],
  il: [-91.51, 36.97, -87.50, 42.51],
  in: [-88.10, 37.77, -84.78, 41.76],
  ia: [-96.64, 40.38, -90.14, 43.50],
  ks: [-102.05, 36.99, -94.59, 40.00],
  ky: [-89.57, 36.50, -81.96, 39.15],
  la: [-94.04, 28.93, -88.82, 33.02],
  me: [-71.08, 43.06, -66.95, 47.46],
  md: [-79.49, 37.91, -75.05, 39.72],
  ma: [-73.51, 41.24, -69.93, 42.89],
  mi: [-90.42, 41.70, -82.41, 48.19],
  mn: [-97.24, 43.50, -89.49, 49.38],
  ms: [-91.66, 30.17, -88.10, 35.00],
  mo: [-95.77, 36.00, -89.10, 40.61],
  mt: [-116.05, 44.36, -104.04, 49.00],
  ne: [-104.05, 40.00, -95.31, 43.00],
  nv: [-120.00, 35.00, -114.04, 42.00],
  nh: [-72.56, 42.70, -70.70, 45.31],
  nj: [-75.56, 38.93, -73.89, 41.36],
  nm: [-109.05, 31.33, -103.00, 37.00],
  ny: [-79.76, 40.50, -71.86, 45.02],
  nc: [-84.32, 33.84, -75.46, 36.59],
  nd: [-104.05, 45.93, -96.55, 49.00],
  oh: [-84.82, 38.40, -80.52, 42.32],
  ok: [-103.00, 33.62, -94.43, 37.00],
  or: [-124.57, 41.99, -116.46, 46.29],
  pa: [-80.52, 39.72, -74.69, 42.27],
  ri: [-71.86, 41.15, -71.12, 42.02],
  sc: [-83.35, 32.03, -78.54, 35.22],
  sd: [-104.06, 42.48, -96.44, 45.95],
  tn: [-90.31, 34.98, -81.65, 36.68],
  tx: [-106.65, 25.84, -93.51, 36.50],
  ut: [-114.05, 37.00, -109.04, 42.00],
  vt: [-73.44, 42.73, -71.46, 45.02],
  va: [-83.68, 36.54, -75.24, 39.47],
  wa: [-124.73, 45.54, -116.92, 49.00],
  wv: [-82.64, 37.20, -77.72, 40.64],
  wi: [-92.89, 42.49, -86.25, 47.08],
  wy: [-111.06, 40.99, -104.05, 45.01],
};

/**
 * Return only the GeoJSON features whose bounding box intersects the
 * given viewport. Used to keep the FeatureCollection passed to Mapbox
 * small enough to JSON.stringify — large multi-state precinct datasets
 * exceed V8's max string length when serialized whole.
 */
export function filterFeaturesByViewport(
  features: GeoJSON.Feature[],
  viewport: [west: number, south: number, east: number, north: number],
): GeoJSON.Feature[] {
  const [vWest, vSouth, vEast, vNorth] = viewport;
  return features.filter((f) => {
    const bbox = featureBbox(f.geometry);
    if (!bbox) return false;
    const [fWest, fSouth, fEast, fNorth] = bbox;
    return vWest <= fEast && vEast >= fWest && vSouth <= fNorth && vNorth >= fSouth;
  });
}

/** Compute [west, south, east, north] for a Polygon or MultiPolygon. */
function featureBbox(
  geom: GeoJSON.Geometry,
): [number, number, number, number] | null {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  const visit = (lng: number, lat: number) => {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  };

  if (geom.type === 'Polygon') {
    for (const ring of geom.coordinates) for (const [lng, lat] of ring) visit(lng, lat);
  } else if (geom.type === 'MultiPolygon') {
    for (const poly of geom.coordinates) {
      for (const ring of poly) for (const [lng, lat] of ring) visit(lng, lat);
    }
  } else {
    return null;
  }

  if (minLng === Infinity) return null;
  return [minLng, minLat, maxLng, maxLat];
}

/**
 * Determine which states intersect a bounding box.
 */
export function getStatesInBounds(
  bounds: [west: number, south: number, east: number, north: number],
): string[] {
  const [bWest, bSouth, bEast, bNorth] = bounds;
  const states: string[] = [];

  for (const [code, [sWest, sSouth, sEast, sNorth]] of Object.entries(STATE_BOUNDS)) {
    // AABB intersection test
    if (bWest <= sEast && bEast >= sWest && bSouth <= sNorth && bNorth >= sSouth) {
      states.push(code);
    }
  }

  return states;
}
